"use client"
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { Patient } from '@/types';
import { useOrganization } from '@/contexts/organization-context';

interface PatientFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPatientCreated: (patient: Omit<Patient, 'id' | 'created_at' | 'mr_number'>) => void;
  onPatientUpdated: (patient: Patient) => void;
  patientToEdit: Patient | null;
}

export function PatientFormModal({ isOpen, onClose, onPatientCreated, onPatientUpdated, patientToEdit }: PatientFormModalProps) {
  const { organization } = useOrganization();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [age, setAge] = useState<number | ''>('');
  const [gender, setGender] = useState<'Male' | 'Female' | 'Other'>('Male');
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (patientToEdit) {
      setName(patientToEdit.name);
      setPhone(patientToEdit.phone);
      setAge(patientToEdit.age);
      setGender(patientToEdit.gender);
    } else {
      setName('');
      setPhone('');
      setAge('');
      setGender('Male');
    }
  }, [patientToEdit]);

  const handleSubmit = async () => {
    if (!organization) {
        toast({ title: "No organization selected", variant: "destructive" });
        return;
    }
    setSaving(true);
    
    const patientData = {
        name,
        phone,
        age: age === '' ? 0 : age,
        gender,
    };

    if (patientToEdit) {
        onPatientUpdated({ ...patientToEdit, ...patientData });
    } else {
        onPatientCreated({ ...patientData, organization_id: organization.id });
    }
    
    setSaving(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{patientToEdit ? 'Edit Patient' : 'Add New Patient'}</DialogTitle>
          <DialogDescription>
            {patientToEdit ? 'Update the details of the patient.' : 'Fill in the details of the new patient.'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="name">Name</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="phone">Phone</Label>
            <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="age">Age</Label>
            <Input id="age" type="number" value={age} onChange={(e) => setAge(parseInt(e.target.value) || '')} />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="gender">Gender</Label>
            <select id="gender" value={gender} onChange={(e) => setGender(e.target.value as any)} className="w-full border rounded px-3 py-2 text-sm">
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 